<i18n>
  {
    "fr": {
      "mobile-phone-label": "Numéro de mobile",
      "phone-label": "Numéro supplémentaire",
      "placeholder": "06 12 34 56 78",
      "errors": {
        "required": "Champ requis"
      }
    },
    "en": {
      "mobile-phone-label": "Mobile phone number",
      "phone-label": "Additional phone number",
      "placeholder": "06 12 34 56 78",
      "errors": {
        "required": "Required field"
      }
    }
  }
</i18n>

<template>
  <v-row no-gutters>
    <v-col class="mr-2">
      <v-row no-gutters>
        <v-col cols="4">
          <v-select
            v-model="mobilePhoneCountryISO"
            class="country-select mr-1"
            :items="countries"
            item-text="native_name"
            item-value="alpha_2_code"
            :menu-props="{'offset-y': true, 'auto': true}"
            height="42px"
            dense
            single-line
            outlined
          >
            <template #item="{ item }">
              <div class="v-list-item__title">
                {{ item.alpha_2_code }} - {{ item.native_name }}
              </div>
            </template>
            <template #selection="{ item }">
              <v-img
                :src="item.flag"
                max-height="20px"
                max-width="20px"
              />
              {{ item.alpha_2_code }}
            </template>
          </v-select>
        </v-col>

        <v-col>
          <v-text-field
            v-model="mobilePhoneNumber"
            :error-messages="mobilePhoneNumberErrors"
            :hint="formatInternational(mobilePhoneNumber, mobilePhoneCountryISO)"
            :placeholder="$t('placeholder')"
            :label="$t('mobile-phone-label')"
            class="phone-number-input"
            clearable
            height="42px"
            dense
            outlined
            @input="mobilePhoneNumberTouch"
            @blur="mobilePhoneNumberTouch"
          >
            <template
              #label
            >
              {{ $t('mobile-phone-label' ) }}
              <span
                v-if="!mobilePhoneNumber"
                class="red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </v-col>

    <v-col class="ml-2">
      <v-row no-gutters>
        <v-col cols="4">
          <v-select
            v-model="phoneCountryISO"
            class="country-select mr-1"
            :items="countries"
            item-text="native_name"
            item-value="alpha_2_code"
            :menu-props="{'offset-y': true, 'auto': true}"
            height="42px"
            dense
            single-line
            outlined
          >
            <template #item="{ item }">
              <div class="v-list-item__title">
                {{ item.alpha_2_code }} - {{ item.native_name }}
              </div>
            </template>
            <template #selection="{ item }">
              <v-img
                :src="item.flag"
                max-height="20px"
                max-width="20px"
              />
              {{ item.alpha_2_code }}
            </template>
          </v-select>
        </v-col>

        <v-col>
          <v-text-field
            v-model="phoneNumber"
            :hint="formatInternational(phoneNumber, phoneCountryISO)"
            :placeholder="$t('placeholder')"
            :label="$t('phone-label')"
            class="phone-number-input"
            clearable
            height="42px"
            dense
            outlined
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'
import LibPhoneNumber from 'google-libphonenumber'

const PhoneNumberFormat = LibPhoneNumber.PhoneNumberFormat
const PhoneNumberUtil = LibPhoneNumber.PhoneNumberUtil.getInstance()

export default {
  name: 'FlightCustomerContactPhones',
  mixins: [validationMixin],
  data: () => ({
    mobilePhone: '',
    mobilePhoneCountryISO: 'FR',
    phone: '',
    phoneCountryISO: 'FR'
  }),
  computed: {
    ...mapGetters({
      countries: 'general/getCountries'
    }),
    mobilePhoneNumber: {
      /**
       * return vuex store state
       * @return {string}
       */
      get () {
        return this.mobilePhone
      },
      /**
       * Format newVal param to E164 phone format then save the new value to the store.
       * Also modify mobilePhone data to newVal
       * @param newVal {string}
       * @return {undefined}
       */
      set (newVal) {
        this.changeMobilePhoneNumber(this.formatE164(newVal, this.mobilePhoneCountryISO))
        this.mobilePhone = newVal
      }
    },
    /**
     * Return an array of string with errors or nothing based on vuelidate state
     * @return {string[]}
     */
    mobilePhoneNumberErrors () {
      const errors = []
      if (!this.$v.mobilePhoneNumber.$dirty) return errors
      !this.$v.mobilePhoneNumber.required && errors.push(this.$i18n.t('errors.required'))
      return errors
    },
    phoneNumber: {
      /**
       * return vuex store state
       * @return {string}
       */
      get () {
        return this.phone
      },
      /**
       * Format newVal param to E164 phone format then save the new value to the store.
       * Also modify mobilePhone data to newVal
       * @param newVal {string}
       * @return {undefined}
       */
      set (newVal) {
        this.changePhoneNumber(this.formatE164(newVal, this.phoneCountryISO))
        this.phone = newVal
      }
    },
    /**
     * Return the opposite of vuelidate invalid parameters (to improve readability of code)
     * This value need to be computed for the watcher to work properly
     * @return {boolean}
     */
    validity () {
      return !this.$v.$invalid
    }
  },
  watch: {
    /**
     * Watch for value change of validity then commit the new value to the store
     * @param newVal {boolean}
     */
    validity (newVal) {
      this.changePhoneNumberValidity(newVal)
    }
  },
  validations: {
    mobilePhoneNumber: { required }
  },
  mounted () {
    this.initMobilePhoneNumber()
  },
  methods: {
    ...mapMutations({
      changePhoneNumber: 'fb/passengersInformation/changePhoneNumber',
      changePhoneNumberValidity: 'fb/passengersInformation/changePhoneNumberValidity',
      changeMobilePhoneNumber: 'fb/passengersInformation/changeMobilePhoneNumber'
    }),
    /**
     * Retrieve customer object from vueX store,
     * then try to assigne value to data based on customer object properties
     * @return {undefined}
     */
    initMobilePhoneNumber () {
      const customer = this.$store.getters['general/getCustomer']
      if (customer.phone_mobile) {
        this.mobilePhoneNumber = customer.phone_mobile
      }
      this.$v.$touch()
    },
    /**
     * Format a phone number to E164 format based on the country iso code.
     * Return undefined if any error is catched
     * @param phone {string}
     * @param countryISO {string}
     * @return {string | undefined}
     */
    formatE164 (phone, countryISO) {
      let formattedNumber
      try {
        formattedNumber = PhoneNumberUtil.format(PhoneNumberUtil.parse(phone, countryISO), PhoneNumberFormat.E164)
      } catch (error) {}
      return formattedNumber
    },
    /**
     * Format a phone number to International format based on the country iso code.
     * Return undefined if any error is catched
     * @param phone {string}
     * @param countryISO {string}
     * @return {string | undefined}
     */
    formatInternational (phone, countryISO) {
      let formattedNumber
      try {
        formattedNumber = PhoneNumberUtil.format(PhoneNumberUtil.parse(phone, countryISO), PhoneNumberFormat.INTERNATIONAL)
      } catch (error) {}
      return formattedNumber
    },
    /**
     * Simply call to vuelidate touch method
     * @return {undefined}
     */
    mobilePhoneNumberTouch () {
      this.$v.mobilePhoneNumber.$touch()
    }
  }
}
</script>

<style scoped>
</style>
